import * as Yup from 'yup';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Link, Stack, Alert, IconButton, InputAdornment, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// routes
import { PATH_AUTH } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import useLocales from '../../../hooks/useLocales';
// components
import Iconify from '../../../components/Iconify';
import { FormProvider, RHFTextField } from '../../../components/hook-form';

// ----------------------------------------------------------------------

export default function LoginForm() {
  const { login } = useAuth();
  const { translate } = useLocales();
  const isMountedRef = useIsMountedRef();
  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    phone: Yup.string().trim().required(translate('login.form.phone_required')),
    password: Yup.string()
      .trim()
      .required(translate('login.form.pass_required'))
      .max(128, translate('login.form.pass_at_max')),
  });

  const defaultValues = {
    phone: '',
    password: '',
    remember: true,
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (data) => {
    try {
      await login(data.phone, data.password);
    } catch (error) {
      console.error(error);
      reset();
      if (isMountedRef.current) {
        if (error.code === 401) {
          setError('afterSubmit', {
            ...error,
            message: translate('login.form.error.user_pass_wrong'),
          });
          return;
        }
        if (error.code === 403) {
          setError('afterSubmit', {
            ...error,
            message: translate('login.form.error.user_forbidden'),
          });
          return;
        }
        setError('afterSubmit', error);
      }
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}

        <RHFTextField
          name="phone"
          label={translate('login.form.phone')}
          inputProps={{
            autoComplete: 'on',
            form: {
              autoComplete: 'on',
            },
          }}
        />

        <RHFTextField
          name="password"
          label={translate('login.form.pass')}
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1.5, mb: 3.5 }}>
        {/* <RHFCheckbox name="remember" label={translate('login.form.remember_me')} /> */}
        <Box />
        <Link component={RouterLink} variant="subtitle2" to={PATH_AUTH.resetPassword}>
          {translate('login.form.forgot_pass')}
        </Link>
      </Stack>

      <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
        {translate('login.form.login')}
      </LoadingButton>
    </FormProvider>
  );
}
